import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { LeadDrilldownModal, StatusBadge, formatSegment, getLeadStageBucket } from "@/components/LeadDrilldownModal";
import { cn } from "@/lib/cn";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
  safeRatio,
} from "@/lib/format";
import type {
  SourceLead,
  SourceLeadsBreakdown,
  SourceRow,
} from "@/lib/types";
import { usePayload } from "@/lib/usePayload";

export function NearbyCampaignsSummary() {
  const { payload, isLoading, viewMode, monthLabel, monthKey } = usePayload();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modalStage, setModalStage] = useState<string | null>(null);

  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;

  // Target campaign keywords that define a true Nearby Campaign
  const nearbyKeywords = [
    "hudson's grill",
    "tulsa zoo",
    "talsa zoo",
    "indian outbound campaign",
    "square pos outbound",
    "nearby campaign",
    "nearby"
  ];

  const getLeadGroup = (lead: any, originalSource: string): string => {
    const campaign = (lead.ga_campaign || "").toLowerCase();
    const salesCampaign = (lead.sales_campaign || "").toLowerCase();
    const source = originalSource.toLowerCase();
    
    if (campaign.includes("hudson's grill") || salesCampaign.includes("hudson's grill") || source.includes("hudson's grill")) {
      return "Hudson's Grill";
    }
    if (campaign.includes("tulsa zoo") || campaign.includes("talsa zoo") || salesCampaign.includes("tulsa zoo") || salesCampaign.includes("talsa zoo") || source.includes("tulsa zoo") || source.includes("talsa zoo")) {
      return "Tulsa Zoo";
    }
    if (campaign.includes("square pos outbound") || salesCampaign.includes("square pos outbound") || source.includes("square pos outbound")) {
      return "Square POS Outbound";
    }
    
    if (lead.sales_campaign && lead.sales_campaign.trim() && !["sales initiative", "marketing initiative", "unidentified"].includes(lead.sales_campaign.trim().toLowerCase())) {
      return lead.sales_campaign.trim();
    }

    return "Sales Initiative";
  };

  // Filter and group the breakdown to target categories
  const breakdown: SourceLeadsBreakdown = {};

  if (payload.source_leads_breakdown) {
    Object.entries(payload.source_leads_breakdown as Record<string, any[]>).forEach(([source, leads]) => {
      leads.forEach((lead) => {
        const campaign = (lead.ga_campaign || "").trim().toLowerCase();
        const salesCampaign = (lead.sales_campaign || "").trim().toLowerCase();
        const sourceLower = source.trim().toLowerCase();

        // Must explicitly have a Nearby Campaign keyword in Sales_Campaign or GA_Campaign
        const isTrueNearby = nearbyKeywords.some(kw => 
          campaign.includes(kw) || 
          salesCampaign.includes(kw) || 
          sourceLower.includes(kw)
        );

        if (isTrueNearby) {
          const groupName = getLeadGroup(lead, source);
          if (!breakdown[groupName]) {
            breakdown[groupName] = [];
          }
          breakdown[groupName].push(lead);
        }
      });
    });
  }

  // Remove empty groups
  Object.keys(breakdown).forEach((key) => {
    if (breakdown[key].length === 0) {
      delete breakdown[key];
    }
  });

  // Calculate totals dynamically based on filtered leads
  const t = {
    mql: 0,
    sql: 0,
    demo_dones: 0,
    sqo: 0,
    wins: 0,
    revenue: 0,
  };

  Object.values(breakdown).forEach((leads) => {
    leads.forEach((lead) => {
      const bucket = getLeadStageBucket(lead);
      const isDemoDone = (lead as any).historical_demo_done || bucket === "SQO" || bucket === "Win";
      if (isDemoDone) t.demo_dones++;

      if (bucket === "MQL") t.mql++;
      else if (bucket === "SQL") t.sql++;
      else if (bucket === "SQO") t.sqo++;
      else if (bucket === "Win") {
        t.wins++;
        t.revenue += (lead as any).revenue || 0;
      }
    });
  });

  // Calculate source summary rows dynamically based on filtered leads
  const rows: SourceRow[] = Object.entries(breakdown).map(([source, leads]) => {
    const rowMql = leads.filter((l) => getLeadStageBucket(l) === "MQL").length;
    const rowSql = leads.filter((l) => getLeadStageBucket(l) === "SQL").length;
    const rowSqo = leads.filter((l) => getLeadStageBucket(l) === "SQO").length;
    const rowWins = leads.filter((l) => getLeadStageBucket(l) === "Win").length;
    const rowRevenue = leads.filter((l) => getLeadStageBucket(l) === "Win").reduce((acc, l) => acc + ((l as any).revenue || 0), 0);
    return {
      source,
      mql: rowMql,
      sql: rowSql,
      sqo: rowSqo,
      wins: rowWins,
      revenue: rowRevenue,
    };
  }).sort((a, b) => b.mql - a.mql);

  const hasAnyBreakdown = Object.values(breakdown).some((list) => list && list.length > 0);
  const displayLabel = viewMode === "current_week" ? (payload.weekly_reporting?.week_label || "Current Week") : monthLabel;

  const toggle = (source: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  };

  const collapseAll = () => setExpanded(new Set());

  const allLeads = Object.entries(breakdown).flatMap(([source, rows]) =>
    rows.map((row) => ({ ...row, source }))
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="font-display text-xl sm:text-2xl font-semibold">Nearby Campaigns Summary</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
          Lead breakdown by source for Nearby Campaigns — {displayLabel}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <KpiCard accent="blue" label="Total MQL" value={formatNumber(t.mql)} onClick={() => setModalStage("MQL")} />
        <KpiCard accent="cyan" label="Total SQL" value={formatNumber(t.sql)} onClick={() => setModalStage("SQL")} />
        <KpiCard accent="indigo" label="Total Demo Done" value={formatNumber(t.demo_dones || 0)} onClick={() => setModalStage("Demo Done")} />
        <KpiCard accent="teal" label="Total SQO" value={formatNumber(t.sqo)} onClick={() => setModalStage("SQO")} />
        <KpiCard accent="green" label="Total Wins" value={formatNumber(t.wins)} onClick={() => setModalStage("Win")} />
        <KpiCard accent="green" label="Total Revenue" value={formatCurrency(t.revenue, { compact: true })} />
      </div>

      <Panel
        title="Leads by Source"
        subtitle={hasAnyBreakdown ? "Click a row to expand individual leads" : "Aggregated funnel performance per source"}
        right={
          hasAnyBreakdown && expanded.size > 0 ? (
            <button
              type="button"
              onClick={collapseAll}
              className="text-xs px-2.5 py-1 rounded-md border border-border text-slate-600 hover:bg-slate-50"
            >
              Collapse all
            </button>
          ) : null
        }
      >
        <SourceTable
          rows={rows}
          breakdown={breakdown}
          expanded={expanded}
          onToggle={toggle}
        />
      </Panel>

      {/* Dedicated Panel: Nearby Campaign CRM Notes & Activity Audit */}
      <NearbyOutreachAudit allLeads={allLeads} />

      {modalStage && (
        <LeadDrilldownModal
          stage={modalStage}
          leads={allLeads}
          monthStr={monthKey}
          onClose={() => setModalStage(null)}
        />
      )}
    </div>
  );
}

function NearbyOutreachAudit({ allLeads }: { allLeads: SourceLead[] }) {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);

  if (allLeads.length === 0) {
    return null;
  }

  // Categorize leads
  const uncontactedLeads = allLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    return st.includes("not contacted") || st.includes("untouched");
  });

  const noResponseLeads = allLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    return st.includes("no response") || st.includes("attempted to contact");
  });

  const gatekeeperLeads = allLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    const reason = ((l as any).reason || l.description || "").toLowerCase();
    return st.includes("decision maker not available") || reason.includes("staff") || reason.includes("manager not available");
  });

  const notInterestedLeads = allLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    return st.includes("not interested") || st.includes("lost");
  });

  const categoryLeadsMap: Record<string, SourceLead[]> = {
    All: allLeads,
    Uncontacted: uncontactedLeads,
    "No Response": noResponseLeads,
    Gatekeeper: gatekeeperLeads,
    "Not Interested": notInterestedLeads,
  };

  const activeLeadsList = categoryLeadsMap[activeCategory] || allLeads;

  return (
    <Panel
      title="Nearby Campaigns CRM Outreach & Friction Audit"
      subtitle="Click any category card to filter & expand detailed CRM rep notes and call summaries"
      right={
        <button
          type="button"
          onClick={() => setShowNotesModal(true)}
          className="text-xs font-semibold px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors shadow-xs flex items-center gap-1.5 cursor-pointer"
        >
          <span>🔍 Expand All Notes ({activeLeadsList.length})</span>
        </button>
      }
    >
      <div className="space-y-5">
        {/* Clickable Category Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <button
            type="button"
            onClick={() => setActiveCategory("All")}
            className={cn(
              "p-3 rounded-card text-left border transition-all cursor-pointer",
              activeCategory === "All"
                ? "bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/20"
                : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-800"
            )}
          >
            <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "All" ? "text-slate-300" : "text-slate-500")}>
              All Nearby Leads
            </div>
            <div className="text-xl font-bold mt-0.5">{allLeads.length}</div>
            <div className={cn("text-[11px] mt-0.5", activeCategory === "All" ? "text-slate-300" : "text-slate-500")}>Full campaign list</div>
          </button>

          <button
            type="button"
            onClick={() => setActiveCategory("Uncontacted")}
            className={cn(
              "p-3 rounded-card text-left border transition-all cursor-pointer",
              activeCategory === "Uncontacted"
                ? "bg-amber-600 text-white border-amber-600 shadow-md ring-2 ring-amber-600/20"
                : "bg-amber-50/70 hover:bg-amber-100/70 border-amber-200 text-amber-950"
            )}
          >
            <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "Uncontacted" ? "text-amber-100" : "text-amber-800")}>
              Uncontacted Leads
            </div>
            <div className="text-xl font-bold mt-0.5">{uncontactedLeads.length}</div>
            <div className={cn("text-[11px] mt-0.5", activeCategory === "Uncontacted" ? "text-amber-100" : "text-amber-700")}>Zero outreach logged</div>
          </button>

          <button
            type="button"
            onClick={() => setActiveCategory("No Response")}
            className={cn(
              "p-3 rounded-card text-left border transition-all cursor-pointer",
              activeCategory === "No Response"
                ? "bg-blue-600 text-white border-blue-600 shadow-md ring-2 ring-blue-600/20"
                : "bg-blue-50/70 hover:bg-blue-100/70 border-blue-200 text-blue-950"
            )}
          >
            <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "No Response" ? "text-blue-100" : "text-blue-800")}>
              Unanswered / Voicemail
            </div>
            <div className="text-xl font-bold mt-0.5">{noResponseLeads.length}</div>
            <div className={cn("text-[11px] mt-0.5", activeCategory === "No Response" ? "text-blue-100" : "text-blue-700")}>Repeated call attempts</div>
          </button>

          <button
            type="button"
            onClick={() => setActiveCategory("Gatekeeper")}
            className={cn(
              "p-3 rounded-card text-left border transition-all cursor-pointer",
              activeCategory === "Gatekeeper"
                ? "bg-purple-600 text-white border-purple-600 shadow-md ring-2 ring-purple-600/20"
                : "bg-purple-50/70 hover:bg-purple-100/70 border-purple-200 text-purple-950"
            )}
          >
            <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "Gatekeeper" ? "text-purple-100" : "text-purple-800")}>
              Gatekeeper Stalls
            </div>
            <div className="text-xl font-bold mt-0.5">{gatekeeperLeads.length}</div>
            <div className={cn("text-[11px] mt-0.5", activeCategory === "Gatekeeper" ? "text-purple-100" : "text-purple-700")}>Owner unavailable</div>
          </button>

          <button
            type="button"
            onClick={() => setActiveCategory("Not Interested")}
            className={cn(
              "p-3 rounded-card text-left border transition-all cursor-pointer",
              activeCategory === "Not Interested"
                ? "bg-rose-600 text-white border-rose-600 shadow-md ring-2 ring-rose-600/20"
                : "bg-rose-50/70 hover:bg-rose-100/70 border-rose-200 text-rose-950"
            )}
          >
            <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "Not Interested" ? "text-rose-100" : "text-rose-800")}>
              Refused / Not Interested
            </div>
            <div className="text-xl font-bold mt-0.5">{notInterestedLeads.length}</div>
            <div className={cn("text-[11px] mt-0.5", activeCategory === "Not Interested" ? "text-rose-100" : "text-rose-700")}>Declined kiosk / app</div>
          </button>
        </div>

        {/* Filtered Lead Preview Grid (Limited to 6 preview cards with click to expand) */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-blue-600"></span>
              <span>Filter: <strong>{activeCategory} Leads</strong> ({activeLeadsList.length} total)</span>
            </h4>
            {activeLeadsList.length > 6 && (
              <button
                type="button"
                onClick={() => setShowNotesModal(true)}
                className="text-xs text-blue-600 hover:text-blue-800 font-bold hover:underline cursor-pointer"
              >
                View all {activeLeadsList.length} records in modal →
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeLeadsList.slice(0, 6).map((lead) => {
              const crmReason = (lead as any).reason || lead.clay_lead_summary || null;
              const descriptionNote = lead.description && !lead.description.includes("what's_your_pos_system") && !lead.description.includes("nav_ref=") ? lead.description : null;
              const displayReason = crmReason || descriptionNote || `${lead.lead_status} (No specific loss reason logged in CRM)`;
              const isUntouched = (lead.lead_status || "").toLowerCase().includes("not contacted");

              return (
                <div
                  key={lead.id || lead.email || lead.name}
                  className={cn(
                    "border rounded-card p-3 text-xs flex flex-col justify-between transition-shadow hover:shadow-sm",
                    isUntouched ? "bg-amber-50/40 border-amber-200" : "bg-white border-slate-200"
                  )}
                >
                  <div>
                    <div className="flex items-center justify-between font-bold text-slate-900 mb-1">
                      <span className="truncate max-w-[160px]" title={lead.name}>
                        {lead.name}
                      </span>
                      <StatusBadge status={lead.lead_status} />
                    </div>

                    <div className="text-slate-500 text-[11px] mb-2 truncate" title={lead.company || "N/A"}>
                      {lead.company || "N/A"} • Rep: <strong className="text-slate-700">{lead.owner || "Unassigned"}</strong>
                    </div>

                    <div className="bg-slate-50 border border-slate-200/70 rounded p-2 text-[11px] line-clamp-3" title={displayReason}>
                      <strong className="text-slate-800">CRM Note:</strong> {displayReason}
                    </div>
                  </div>

                  <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400">
                      {lead.received_date ? formatDate(lead.received_date) : "MQL"}
                    </span>
                    {lead.zoho_url && (
                      <a
                        href={lead.zoho_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 font-bold hover:underline"
                      >
                        Zoho CRM →
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {activeLeadsList.length > 6 && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setShowNotesModal(true)}
                className="inline-flex items-center space-x-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-semibold transition-colors cursor-pointer"
              >
                <span>Showing 6 of {activeLeadsList.length} {activeCategory} leads. Click to expand full list</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Expanded Full List */}
      {showNotesModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setShowNotesModal(false)}
        >
          <div
            className="bg-white rounded-card shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-slate-50 shrink-0">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Nearby Campaigns CRM Notes Audit — {activeCategory} ({activeLeadsList.length} Leads)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Complete rep call takeaways, voicemails, and status reason logs from Zoho CRM
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowNotesModal(false)}
                className="px-3 py-1 bg-slate-200 hover:bg-slate-300 rounded text-xs font-bold text-slate-700 cursor-pointer"
              >
                Close ✕
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-3 flex-1">
              {activeLeadsList.map((lead) => {
                const crmReason = (lead as any).reason || lead.clay_lead_summary || null;
                const descriptionNote = lead.description && !lead.description.includes("what's_your_pos_system") && !lead.description.includes("nav_ref=") ? lead.description : null;
                const displayReason = crmReason || descriptionNote || `${lead.lead_status} (No specific loss reason logged in CRM)`;

                return (
                  <div key={lead.id || lead.email || lead.name} className="border border-slate-200 rounded-card p-3 text-xs bg-white flex flex-col justify-between space-y-2">
                    <div className="flex items-center justify-between font-bold text-slate-900">
                      <span>{lead.name} ({lead.company || "N/A"}) — <span className="text-slate-600 font-normal">Rep: {lead.owner || "Unassigned"}</span></span>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-500 text-[11px] font-medium">{(lead as any).sales_campaign || lead.ga_campaign || "Nearby"}</span>
                        <StatusBadge status={lead.lead_status} />
                      </div>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-[11px]">
                      <strong className="text-slate-800">CRM Activity Note:</strong> {displayReason}
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                      <span>Received: {lead.received_date ? formatDate(lead.received_date) : "MQL"}</span>
                      {lead.zoho_url && (
                        <a href={lead.zoho_url} target="_blank" rel="noreferrer" className="text-blue-600 font-bold hover:underline">
                          Open in Zoho CRM →
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

interface SourceTableProps {
  rows: SourceRow[];
  breakdown: SourceLeadsBreakdown;
  expanded: Set<string>;
  onToggle: (source: string) => void;
}

function SourceTable({ rows, breakdown, expanded, onToggle }: SourceTableProps) {
  if (rows.length === 0) {
    return <div className="text-sm text-slate-500 py-6 text-center">No sources found for these campaigns in this period.</div>;
  }

  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left label-xs border-b border-border">
            <th className="font-medium px-5 py-2.5">Source</th>
            <th className="font-medium px-3 py-2.5 text-right">MQL</th>
            <th className="font-medium px-3 py-2.5 text-right">SQL</th>
            <th className="font-medium px-3 py-2.5 text-right">SQO</th>
            <th className="font-medium px-3 py-2.5 text-right">Wins</th>
            <th className="font-medium px-3 py-2.5 text-right">Revenue</th>
            <th className="font-medium px-5 py-2.5 text-right">MQL → SQL</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const conv = safeRatio(r.sql, r.mql);
            const tone =
              conv == null
                ? "text-slate-400"
                : conv >= 0.2
                  ? "text-green-600"
                  : conv >= 0.05
                    ? "text-amber-600"
                    : "text-slate-400";
            const leads = breakdown[r.source] || [];
            const canExpand = leads.length > 0;
            const isOpen = expanded.has(r.source);
            return (
              <SourceRowGroup
                key={r.source}
                row={r}
                leads={leads}
                canExpand={canExpand}
                isOpen={isOpen}
                onToggle={onToggle}
                conv={conv}
                tone={tone}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface SourceRowGroupProps {
  row: SourceRow;
  leads: SourceLead[];
  canExpand: boolean;
  isOpen: boolean;
  onToggle: (source: string) => void;
  conv: number | null;
  tone: string;
}

function SourceRowGroup({ row, leads, canExpand, isOpen, onToggle, conv, tone }: SourceRowGroupProps) {
  const handleClick = canExpand ? () => onToggle(row.source) : undefined;
  return (
    <>
      <tr
        className={cn(
          "border-b border-border last:border-0",
          canExpand && "cursor-pointer hover:bg-slate-50/60",
          isOpen && "bg-blue-50/60"
        )}
        onClick={handleClick}
      >
        <td className="px-5 py-3 font-medium">
          <span className="inline-flex items-center gap-1.5">
            {canExpand ? (
              <span
                aria-hidden
                className={cn(
                  "inline-flex h-4 w-4 items-center justify-center text-slate-400 transition-transform",
                  isOpen && "rotate-90 text-blue-600"
                )}
              >
                ▸
              </span>
            ) : (
              <span aria-hidden className="inline-flex h-4 w-4" />
            )}
            <span>{row.source}</span>
            <span className="ml-1 text-xs text-slate-500"><span className="numeric-value">{row.mql}</span> {row.mql === 1 ? "MQL" : "MQLs"}</span>
          </span>
        </td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.mql)}</td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.sql)}</td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.sqo)}</td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.wins)}</td>
        <td className="px-3 py-3 text-right numeric-value">
          {row.revenue ? formatCurrency(row.revenue, { compact: true }) : "—"}
        </td>
        <td className={cn("px-5 py-3 text-right numeric-value", tone)}>
          {conv != null ? formatPercent(conv) : "0.0%"}
        </td>
      </tr>
      {canExpand && isOpen && (
        <tr className="bg-blue-50/40 border-b border-border">
          <td colSpan={7} className="px-5 py-3">
            <SourceLeadDetails leads={leads} source={row.source} />
          </td>
        </tr>
      )}
    </>
  );
}

function getReferralFrom(lead: any): string {
  if (lead.ga_source || lead.ga_medium) {
    return `${lead.ga_source || "-"} / ${lead.ga_medium || "-"}`;
  }
  if (lead.ga_campaign) return lead.ga_campaign;
  if (lead.landing_page) return lead.landing_page;
  if (lead.page_referrer) return lead.page_referrer;
  if (lead.referrer) return lead.referrer;
  if (lead.referral_from) return lead.referral_from;
  if (lead.clay_lead_summary) return lead.clay_lead_summary;
  if (lead.description) return lead.description;
  return "-";
}

function SourceLeadDetails({ leads, source }: { leads: SourceLead[]; source: string }) {
  const isReferral = source === "Referral";
  const isPaidSearch = source === "Paid Search";
  const isPaidSocial = source === "Paid Social";
  return (
    <div className="overflow-x-auto rounded-md border border-blue-200/70 bg-white">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left label-xs text-blue-700 bg-blue-50/70 border-b border-blue-200/70">
            <th className="font-medium px-3 py-2">Name</th>
            <th className="font-medium px-3 py-2">Email</th>
            <th className="font-medium px-3 py-2">Company</th>
            {isReferral && <th className="font-medium px-3 py-2">Referral From</th>}
            {isPaidSearch && (
              <>
                <th className="font-medium px-3 py-2">GCLID</th>
                <th className="font-medium px-3 py-2">Keyword</th>
              </>
            )}
            {(isPaidSocial || source === "Inbound") && <th className="font-medium px-3 py-2">Campaign</th>}
            <th className="font-medium px-3 py-2">Owner</th>
            <th className="font-medium px-3 py-2">Status</th>
            <th className="font-medium px-3 py-2">Segment</th>
            <th className="font-medium px-3 py-2">Date</th>
          </tr>
        </thead>
        <tbody>
          {[...leads]
            .sort((a, b) => {
              const aTime = a.received_date ? new Date(a.received_date).getTime() : 0;
              const bTime = b.received_date ? new Date(b.received_date).getTime() : 0;
              return bTime - aTime;
            })
            .map((lead) => {
              const hasLink = Boolean(lead.zoho_url);
              return (
                <tr
                  key={`${lead.module}-${lead.id}`}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                >
                  <td className="px-3 py-2 font-medium text-ink-900">
                    {hasLink ? (
                      <a
                        href={lead.zoho_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Open in Zoho"
                        className="hover:underline text-blue-600"
                      >
                        {lead.name || "—"}
                      </a>
                    ) : (
                      lead.name || "—"
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {lead.email || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {lead.company || <span className="text-slate-400">—</span>}
                  </td>
                  {isReferral && (
                    <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={getReferralFrom(lead)}>
                      {getReferralFrom(lead)}
                    </td>
                  )}
                  {isPaidSearch && (
                    <>
                      <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={lead.gclid || "-"}>
                        {lead.gclid || "-"}
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={lead.keyword || "-"}>
                        {lead.keyword || "-"}
                      </td>
                    </>
                  )}
                  {(isPaidSocial || source === "Inbound") && (
                    <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={lead.sales_campaign || lead.ga_campaign || "-"}>
                      {lead.sales_campaign || lead.ga_campaign || "-"}
                    </td>
                  )}
                  <td className="px-3 py-2 text-slate-700">
                    {lead.owner || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={lead.lead_status} />
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {formatSegment((lead as SourceLead & { segment?: string }).segment)}
                  </td>
                  <td className="px-3 py-2 numeric-value text-slate-600">
                    {lead.received_date ? formatDate(lead.received_date) : <span className="text-slate-400">—</span>}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
